<template>
  <grid-container-fi
    v-if="assistedService?.user"
    :class="{
      'assisted-service-module-fi': true,
    }"
    grid-base-class="fi-grid-cols-12 fi-gap-none"
    :container="false"
  >
    <div class="assisted-service-module-fi__content fi-col-span-12">
      <div class="assisted-service-module-fi__label">
        {{
          $dict.get(
            salesforce
              ? 'assisted-Service-Module.Module-Label-Salesforce'
              : 'assisted-Service-Module.Module-Label',
          )
        }}
      </div>
      <div
        :class="{
          'assisted-service-module-fi__session': true,
          'assisted-service-module-fi__session--quote': salesforce,
        }"
      >
        <div
          v-if="!salesforce"
          class="assisted-service-module-fi__user-info"
          data-test="assisted-service-session-info"
        >
          <div class="assisted-service-module-fi__user-name-sm">
            {{ assistedService.user.name }}
            -
            {{ assistedService.user.customerId }}
          </div>
          <div class="assisted-service-module-fi__user-uid-sm">
            {{ assistedService.user.displayUid }}
          </div>
          <div class="assisted-service-module-fi__user-md">
            {{ assistedService.user.name }}
            -
            {{ assistedService.user.displayUid }}
            -
            {{ assistedService.user.customerId }}
          </div>
        </div>
        <div
          class="assisted-service-module-fi__quit"
          data-test="end-assisted-service-session"
          @click="onQuit"
        >
          <div class="assisted-service-module-fi__quit-text">
            {{ $dict.get('assisted-Service-Module.Quit') }}
          </div>
          <icon-fi icon="power" />
        </div>
      </div>
    </div>
    <div
      v-if="salesforce"
      class="fi-col-span-12 fi-grid fi-min-h-[80px] fi-grid-cols-12 fi-gap-small fi-bg-grey-20 fi-px-30 fi-py-9 fi-text-18 fi-text-black fi-semibold fi-text-size-df-16 md:fi-gap-large"
    >
      <div class="fi-col-span-full sm:fi-col-span-6 md:fi-col-span-4 lg:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote">
          {{ $dict.get('assisted-Service-Module.Customer-Label') }}
        </div>
        <div>
          {{ assistedService.user?.company?.name }}
        </div>
      </div>
      <div class="fi-col-span-full sm:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote">
          {{ $dict.get('online-Shop.Global-Labels.Contact-Details.Customer-Number') }}
        </div>
        <div>
          {{ salesforce.orgUnitId }}
        </div>
      </div>
      <div class="fi-col-span-full sm:fi-col-span-4 md:fi-col-span-6 lg:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote">
          {{ $dict.get('assisted-Service-Module.Search.Customer-Distribution') }}
        </div>
        <div v-if="salesforce.distributionChannel && salesforce.division">
          {{ salesforce.distributionChannel }} / {{ salesforce.division }} {{ divisionDisplay }}
        </div>
      </div>
      <div class="fi-col-span-full sm:fi-col-span-5 md:fi-col-span-4 lg:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote">
          {{ $dict.get('assisted-Service-Module.Contact') }}
        </div>
        <div>
          {{ salesforce.cart?.salesforceContact?.firstName }}
          {{ salesforce.cart?.salesforceContact?.lastName }}
        </div>
      </div>
      <div class="fi-col-span-full sm:fi-col-span-5 md:fi-col-span-6 lg:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote">
          {{ $dict.get('online-Shop.Global-Labels.Contact-Details.Email') }}
        </div>
        <div>{{ salesforce.cart?.salesforceContact?.email }}</div>
      </div>
      <div class="fi-col-span-full fi-text-right sm:fi-col-span-2">
        <div class="assisted-service-module-fi__label--quote fi-text-right">
          {{ $dict.get('assisted-Service-Module.Type') }}
        </div>
        <div>{{ typeDisplay }}</div>
      </div>
    </div>
  </grid-container-fi>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useSalesforceInformation } from '@/composables/salesforceInformation';
import type { AssistedServiceData } from '@/lib/fischerPlugin';
import { salesforceItemName } from '@/lib/fischerPlugin';
import ClientStorage from '@/utils/clientStorage';

import { useAssistedServiceLogin } from './assistedServiceLogin';

defineOptions({
  name: 'AssistedServiceModuleFi',
});

const fischer = useFischer();
const dict = useDict();
const store = useStore();
const { postAdminTokenInSession } = useAssistedServiceLogin();
const { typeDisplay } = useSalesforceInformation();

const salesforce = ref<any>(null);
const assistedService = fischer.user.getAssistedService();

const onQuit = async () => {
  if (!assistedService) {
    return;
  }

  const accessToken = fischer.user.accessToken();
  const idToken = fischer.user.idToken();

  const newAssistedService: AssistedServiceData = {
    ...assistedService,
    user: undefined,
  };

  try {
    fischer.setAssistedService(newAssistedService);

    if (salesforce.value) {
      try {
        window.sessionStorage.removeItem(salesforceItemName);
      } catch (_error) {
        // noop
      }
      const logoutUrl = fischer.pages.logout().url;
      if (logoutUrl) {
        window.location.href = logoutUrl;
        return;
      } else {
        console.error('missing logout url');
      }
    }

    const { LoginSuccessPage } = await postAdminTokenInSession(accessToken, idToken);
    ClientStorage.setValue('impersonatedUser', JSON.stringify(newAssistedService.user), true);
    window.location.href = LoginSuccessPage || '/';
  } catch (error) {
    console.error('Error ending assisted service', error);
  }
};

const setSalesforceData = () => {
  salesforce.value = fischer.user.getSalesforce();
  if (salesforce.value) {
    store.commit('navigation/SET_ASSISTED_SERVICE_SALESFORCE', true);
  }
};

if (!IS_SSR && assistedService) {
  if (!fischer.user.authenticated()) {
    // session expired, clear assisted service
    fischer.setAssistedService();
  } else {
    const isAssistedServiceActive = Boolean(assistedService.user);
    store.commit('navigation/SET_ASSISTED_SERVICE_ACTIVE', isAssistedServiceActive);
    if (isAssistedServiceActive) {
      setSalesforceData();
    }
  }
}

const divisionDisplay = computed<string | undefined>(() => {
  const division = salesforce.value?.division;
  if (!division) {
    return undefined;
  }
  const displayName = dict.get(`online-Shop.Global-Labels.Division-Display.${division}`);
  if (!displayName) {
    return undefined;
  }
  return `(${displayName})`;
});
</script>

<style scoped src="./assisted-service-module-fi" />
